import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { WaitingRoomDoctorStatus } from '@/core/api/waiting-room'
import { useGetWaitingRoomDoctorsQuery, useGetWaitingRoomDoctorsStatusQuery } from '@/core/services/waiting-room'
import { selectDoctorStatuses } from '@/redux/reducers/waiting-room.reducer'
import { useAppSelector } from '@/redux/rootReducer'

export type LiveDoctorStatus = 'available' | 'in_consultation'

type LiveDoctorStatusEntry = { status: 'available' | 'in_consultation' | 'offline'; consultationId?: string; lastUpdated: number }

// Backend Redis presence entries expire after 60s without a heartbeat; poll at half that so the
// snapshot never goes stale for users outside the room's socket channel (they get no status events).
const POLL_INTERVAL_MS = 30_000

/**
 * Merge the HTTP snapshot with the socket-fed Redux map. The viewer's doctor roster gates
 * both sources, including doctors absent from the online snapshot.
 * A Redux entry overrides it only when at least as fresh (i.e. a socket event
 * received after the fetch). A doctor absent from the result is offline (or not yet tracked).
 */
export function mergeDoctorStatuses(
  snapshot: WaitingRoomDoctorStatus[] | undefined,
  live: Record<string, LiveDoctorStatusEntry>,
  snapshotAt: number | undefined,
  memberDoctorIds: string[]
): Record<string, LiveDoctorStatus> {
  const allowedDoctors = new Set(memberDoctorIds)
  const merged: Record<string, LiveDoctorStatus> = {}
  snapshot?.forEach(({ doctorId, status }) => {
    if (allowedDoctors.has(doctorId)) merged[doctorId] = status
  })
  Object.entries(live).forEach(([doctorId, { status, lastUpdated }]) => {
    if (!allowedDoctors.has(doctorId)) return
    if (lastUpdated < (snapshotAt ?? 0)) return
    if (status === 'offline') delete merged[doctorId]
    else merged[doctorId] = status
  })
  return merged
}

/** Room.doctors holds raw ids for staff users but populated Staff objects for admins. */
export function toDoctorIds(doctors: Array<string | { _id: string }> | undefined): string[] {
  return (doctors ?? []).map((doctor) => (typeof doctor === 'string' ? doctor : doctor._id))
}

/** Honest count: the room's member doctors that are online AND idle right now. */
export function countAvailableDoctors(memberDoctorIds: string[], statuses: Record<string, LiveDoctorStatus>): number {
  return memberDoctorIds.filter((doctorId) => statuses[doctorId] === 'available').length
}

/**
 * Live per-doctor statuses for a waiting room, merged from the doctors-status endpoint (polled)
 * and the socket-fed Redux map. `isReady` is false until the snapshot and viewer roster arrive — callers
 * should show a placeholder rather than guessing availability from room membership.
 */
export function useRoomDoctorStatuses(waitingRoomId: string | undefined) {
  const { currentData: data, fulfilledTimeStamp, isSuccess } = useGetWaitingRoomDoctorsStatusQuery(waitingRoomId ?? skipToken, {
    pollingInterval: POLL_INTERVAL_MS,
  })
  // Roster changes are rare and invalidated through the RTK cache tags; only the status snapshot polls.
  const { currentData: doctors, isSuccess: isRosterReady } = useGetWaitingRoomDoctorsQuery(waitingRoomId ?? skipToken)
  const liveStatuses = useAppSelector(selectDoctorStatuses)

  const statuses = useMemo(
    () => mergeDoctorStatuses(data, liveStatuses, fulfilledTimeStamp, toDoctorIds(doctors)),
    [data, liveStatuses, fulfilledTimeStamp, doctors]
  )

  return { statuses, isReady: isSuccess && isRosterReady && !!data && !!doctors }
}
